import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Loader2 } from "lucide-react";
import { fetchClient, useUser } from "../lib/api-client";
import toast from "react-hot-toast";

const MIN_AMOUNT_POUNDS = 0.5;

const amountField = z
  .number({ error: "Amount is required" })
  .min(MIN_AMOUNT_POUNDS, "Minimum payment is £0.50");

const paymentTypeSchema = z.enum(["snack_bar", "log_book"]);

const guestPayFormSchema = z.object({
  name: z.string().min(1, "Name is required").max(200),
  paymentType: paymentTypeSchema,
  amountPounds: amountField,
});

const authenticatedPayFormSchema = z.object({
  paymentType: paymentTypeSchema,
  amountPounds: amountField,
});

type GuestPayFormValues = z.infer<typeof guestPayFormSchema>;
type AuthenticatedPayFormValues = z.infer<typeof authenticatedPayFormSchema>;
type PaymentType = z.infer<typeof paymentTypeSchema>;

async function startPaymentSession(body: {
  name?: string;
  paymentType: PaymentType;
  amountCents: number;
}) {
  const { data: response, response: httpResponse, error } =
    await fetchClient.POST("/api/public/payments/session", { body });

  if (!httpResponse.ok || error || !response?.sessionUrl) {
    toast.error("Something went wrong. Please try again.");
    return;
  }

  window.location.href = response.sessionUrl;
}

const fieldClassName =
  "mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 sm:text-sm";

function GuestPayForm() {
  const [submitLoading, setSubmitLoading] = useState(false);
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<GuestPayFormValues>({
    resolver: zodResolver(guestPayFormSchema),
    defaultValues: {
      name: "",
      paymentType: "snack_bar",
      amountPounds: undefined,
    },
  });

  const onSubmit = async (data: GuestPayFormValues) => {
    setSubmitLoading(true);
    try {
      await startPaymentSession({
        name: data.name,
        paymentType: data.paymentType,
        amountCents: Math.round(data.amountPounds * 100),
      });
    } catch {
      toast.error("Failed to start payment. Please try again.");
    } finally {
      setSubmitLoading(false);
    }
  };

  return (
    <form className="space-y-6" onSubmit={handleSubmit(onSubmit)}>
      <div>
        <label
          htmlFor="name"
          className="block text-sm font-medium text-gray-700"
        >
          Name
        </label>
        <input
          id="name"
          type="text"
          autoComplete="name"
          className={fieldClassName}
          {...register("name")}
        />
        {errors.name && (
          <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>
        )}
      </div>

      <div>
        <label
          htmlFor="paymentType"
          className="block text-sm font-medium text-gray-700"
        >
          Payment type
        </label>
        <select
          id="paymentType"
          className={fieldClassName}
          {...register("paymentType")}
        >
          <option value="snack_bar">Snack Bar</option>
          <option value="log_book">Log Book</option>
        </select>
        {errors.paymentType && (
          <p className="mt-1 text-sm text-red-600">
            {errors.paymentType.message}
          </p>
        )}
      </div>

      <div>
        <label
          htmlFor="amountPounds"
          className="block text-sm font-medium text-gray-700"
        >
          Amount (£)
        </label>
        <input
          id="amountPounds"
          type="number"
          inputMode="decimal"
          step="0.01"
          min={MIN_AMOUNT_POUNDS}
          placeholder="0.50"
          className={fieldClassName}
          {...register("amountPounds", { valueAsNumber: true })}
        />
        {errors.amountPounds && (
          <p className="mt-1 text-sm text-red-600">
            {errors.amountPounds.message}
          </p>
        )}
        <p className="mt-1 text-sm text-gray-500">Minimum £0.50</p>
      </div>

      <SubmitButton loading={submitLoading} />
    </form>
  );
}

function AuthenticatedPayForm() {
  const [submitLoading, setSubmitLoading] = useState(false);
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<AuthenticatedPayFormValues>({
    resolver: zodResolver(authenticatedPayFormSchema),
    defaultValues: {
      paymentType: "snack_bar",
      amountPounds: undefined,
    },
  });

  const onSubmit = async (data: AuthenticatedPayFormValues) => {
    setSubmitLoading(true);
    try {
      await startPaymentSession({
        paymentType: data.paymentType,
        amountCents: Math.round(data.amountPounds * 100),
      });
    } catch {
      toast.error("Failed to start payment. Please try again.");
    } finally {
      setSubmitLoading(false);
    }
  };

  return (
    <form className="space-y-6" onSubmit={handleSubmit(onSubmit)}>
      <div>
        <label
          htmlFor="paymentType"
          className="block text-sm font-medium text-gray-700"
        >
          Payment type
        </label>
        <select
          id="paymentType"
          className={fieldClassName}
          {...register("paymentType")}
        >
          <option value="snack_bar">Snack Bar</option>
          <option value="log_book">Log Book</option>
        </select>
        {errors.paymentType && (
          <p className="mt-1 text-sm text-red-600">
            {errors.paymentType.message}
          </p>
        )}
      </div>

      <div>
        <label
          htmlFor="amountPounds"
          className="block text-sm font-medium text-gray-700"
        >
          Amount (£)
        </label>
        <input
          id="amountPounds"
          type="number"
          inputMode="decimal"
          step="0.01"
          min={MIN_AMOUNT_POUNDS}
          placeholder="0.50"
          className={fieldClassName}
          {...register("amountPounds", { valueAsNumber: true })}
        />
        {errors.amountPounds && (
          <p className="mt-1 text-sm text-red-600">
            {errors.amountPounds.message}
          </p>
        )}
        <p className="mt-1 text-sm text-gray-500">Minimum £0.50</p>
      </div>

      <SubmitButton loading={submitLoading} />
    </form>
  );
}

function SubmitButton({ loading }: { loading: boolean }) {
  return (
    <div>
      <button
        type="submit"
        disabled={loading}
        className="flex w-full justify-center rounded-md border border-transparent bg-primary-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {loading ? (
          <Loader2 className="w-5 h-5 animate-spin" />
        ) : (
          "Pay with card"
        )}
      </button>
    </div>
  );
}

export function Pay() {
  const { data: user, isAuthenticated, isLoading: isUserLoading } = useUser();
  const isLoggedIn = isAuthenticated && !!user;

  return (
    <div className="space-y-8 max-w-lg mx-auto">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Make a payment</h1>
        <p className="text-gray-600 mt-1">
          Pay for the bar or other club honesty payments. You will be redirected
          to Stripe to complete payment securely.
        </p>
      </div>

      <div className="w-full">
        <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10 w-full">
          {isUserLoading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
            </div>
          ) : isLoggedIn ? (
            <AuthenticatedPayForm />
          ) : (
            <GuestPayForm />
          )}
        </div>
      </div>
    </div>
  );
}
